import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { Folder } from '../../models/site-content/folder.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FolderService } from '../../services/folder.service';
import { WorkflowTransition } from '../../models/workflow-transition.model';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FolderFormTemplate } from '../../models/folder-form-template.model';
import { Form } from '../../models/form-builder/form.model';
import { IDrawerComponentLiaison } from '../../interfaces/idrawer-component-liaison';
import { DrawerComponentBase } from '../drawer-component-base/drawer-component-base.component';
import { CurrentUserService } from '../../../security/current-user.service';
import { CurrentSiteService } from '../../services/current-site.service';
import { DataCollection } from '../../models/site-content/data-collection.model';

@Component({
    selector: 'app-folder-details',
    templateUrl: './folder-details.component.html',
    styleUrls: ['./folder-details.component.scss', '../properties.scss'],
    standalone: false
})
export class FolderDetailsComponent extends DrawerComponentBase implements OnInit {
    @Input() folder: Folder;
    @Input() mode: string;
    @Input() drawerComponentLiaison: IDrawerComponentLiaison = null;

    @Output() save: EventEmitter<Folder> = new EventEmitter();
    @Output() workflowStateUpdate: EventEmitter<Folder> = new EventEmitter();
    @Output() delete: EventEmitter<Folder> = new EventEmitter();
    @Output() close: EventEmitter<Folder> = new EventEmitter();

    folderFormTemplate: FolderFormTemplate;
    selectedFormTemplateIsMandatory: boolean;
    ngFormGroup: UntypedFormGroup; // naming it this to avoid confusion with Collect's Forms vs NG's Forms

    public constructor(private fb: UntypedFormBuilder, private folderService: FolderService, public currentUserService: CurrentUserService, private currentSiteService: CurrentSiteService, public dialog: MatDialog) {
        super();
    }

    public ngOnInit() {
        // This call to super.ngOnInit() is crucial. Without it, the drawerComponentLiaison Input variable (see above) won't have chance to configure this component or subscribe to
        // its events (such as save, workflowStateUpdate etc.)
        // This call performs the critically important step of invoking a DrawerComponent's drawerComponentLiaison.drawerComponentInitialized()
        // method and passing in this DrawerComponent. That's how Liaison objects get a handle to the drawer components they work with (Liaise objects having the job
        // of liaising between a component (such as Structure as in the present case) and a drawer component (a component that gets rendered on a drawer)
        // Perhaps consider renaming DrawerComponentBase.ngOnInit() to something which makes what it does clear
        super.ngOnInit(); // Important to call this as the first line in this method.

        this.buildFormGroup();
    }

    public onSave(): void {
        let formValue = this.ngFormGroup.value;
        let folder: Folder = formValue;

        if (formValue.selectedFormTemplateId == 0) {
            folder.folderFormTemplate = null;
        } else {
            folder.folderFormTemplate = new FolderFormTemplate(this.folder.folderFormTemplate?.id, formValue.selectedFormTemplateId, formValue.id, formValue.selectedFormTemplateIsMandatory);
        }
        this.save.emit(this.ngFormGroup.value);
    }

    public onDelete(): void {
        this.delete.emit(this.folder);
    }

    public onClose(): void {
        this.close.emit();
    }

    public reset(): void {
        if (!this.ngFormGroup.dirty) {
            this.onClose();
        }
        this.ngFormGroup.reset(this.folder);
    }

    public transitionWorkflow(evt: WorkflowTransition): void {
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: "400px",
            data: new ConfirmationDialogModel(
                `Change Workflow State`,
                `Are you sure you want to apply the "${evt.name}" transition?`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                this.folder.selectedWorkflowTransition = evt.name;
                this.workflowStateUpdate.emit(this.folder);
            }
        });
    }

    public GetNoneOptionText(): string {
        let templateFolderId = this.folder?.folderFormTemplate?.folderId;
        if (templateFolderId && this.folder.id != templateFolderId) {
            return "None (Inherit)";
        } else {
            return "None";
        }
    }

    public GetOptionText(form: Form): string {
        let templateFolderId = this.folder?.folderFormTemplate?.folderId;
        let formId = this.folder?.folderFormTemplate?.formId;
        let name = form.name;
        // If the folderFormTemplate's FormId is the same but it's FolderId is different, then it is inherited so indicate that in the option's name
        if ((formId && form.id == formId) && (templateFolderId && this.folder.id != templateFolderId)) {
            name = `${form.name} (Inherited)`;
        }
        return name;
    }

    public get InheritsFolderFormTemplate(): boolean {
        let templateFolderId = this.folder?.folderFormTemplate?.folderId;
        return templateFolderId && this.folder.id != templateFolderId;
    }

    public get Folder() {
        return this.folder;
    }

    public get FolderFormTemplate() {
        return this.folder.folderFormTemplate;
    }

    public get SelectedFormTemplateId() {
        return this.ngFormGroup.value.selectedFormTemplateId;
    }

    public get UserIsSysAdmin(): boolean {
        return this.currentUserService.user.isSystemAdmin;
    }

    public get ShowFolderWorkflowProperties(): boolean {
        return !this.currentSiteService.Site.useSimplifiedPermissionsModel;
    }

    // Helper methods.
    private buildFormGroup() {
        this.ngFormGroup = this.fb.group({
            name: [this.folder.name, Validators.required],
            description: [this.folder.description],
            id: [this.folder.id],
            dataCollectionId: [this.folder.dataCollectionId],
            hideExportToExcel: [{value: this.folder.HideExportToExcel, disabled: this.folder.HideExportToExcelIsInherited}],
            hideUpload: [{value: this.folder.HideUpload, disabled:this.folder.HideUploadIsInherited }],
            hideDownload: [{ value: this.folder.HideDownload, disabled:this.folder.HideDownloadIsInherited }],
            hidePrintForm: [{ value: this.folder.HidePrintForm, disabled: this.folder.HidePrintFormIsInherited }],
            parentFolderId: [this.folder.parentFolderId],
            workflowId: [this.folder.workflowId],
            selectedFormTemplateId: [this.folder.folderFormTemplate?.formId || 0],
            selectedFormTemplateIsMandatory: [this.folder.folderFormTemplate?.isMandatory == true]
        });
    }

}
